import { convertToStandardPath } from "csi/core/extendedPathService";
import sessionConstants from "csi/sessionConstants";
import { C8AttributeId, C8NodeType, C8PrimitiveValue } from "csi/typings";
import util from "csi/util";
import { pathRemoveStep } from "csi/utility/pathUtility";
import xml from "csi/xml";
import _Query from "./_Query";

const AGENT_PATH_SUFFIX = "/Parent:__Suppliers__";
const ATTR_SUPPLIER = "Supplier";
const RE_SUPPLIER_LAST_STEP = /Child:Supplier$/;

interface QueryFilterParams {
	Dimension?: number;
	Flag?: number;
	Index?: number | string;
	Path?: string;

	DValue?: C8PrimitiveValue;
	NValue?: C8PrimitiveValue;
	RValue?: C8PrimitiveValue;
	SValue?: C8PrimitiveValue;
	Value?: C8PrimitiveValue;
}

class Query extends _Query {
	/** true if query accelerator should not process the query */
	bDirect = false;

	/** document holding query elements */
	bServerOnly = false;

	constructor(doc?: XMLDocument, element?: Element) {
		super(doc, element);
	}

	/**
	 * Return root tag for query
	 */
	override get rootTag(): string {
		return "Query";
	}

	/**
	 * Return true if client can process the query
	 */
	canClientPerformQuery(): boolean {
		// access checks cannot be done on client; if query filters involves paths, then we also cannot rely on client as it is possible user does
		// not have access to some of the nodes along those paths. Because of that, the client cannot behave the same as the server.
		return !this.bServerOnly && !this.getPaths().length;
	}

	/**
	 * Return a copy of this query
	 */
	copy(): Query {
		const q = new Query();
		q.importQuery(this);
		return q;
	}

	/**
	 * Same as toString, but with Direct="true" attribute to disable query accelerator
	 */
	getDirectString(): string {
		this.element.setAttribute("Direct", "true");
		const s = this.toString();
		this.element.removeAttribute("Direct");
		return s;
	}

	/**
	 * Return element in query after any Type filters, if any
	 */
	getElementAfterTypeFilter(): Element {
		const typeElements = this.getElements().filter((e) => e.getAttribute?.(xml.FILTER_PROP_PARAMETER) === xml.PROP_TYPE);
		return typeElements.pop()?.nextSibling as Element;
	}

	/**
	 * Add paths used in query and return array (same as given)
	 */
	getPaths(paths?: string[]): string[] {
		paths ||= [];

		this._iterateElement((xn) => {
			const p = xn.getAttribute?.(xml.FILTER_PROP_PATH);
			if (p) {
				paths.push(p);
			}
		});

		return paths;
	}

	/**
	 * Return true if query contains expressions
	 */
	hasExpression(): boolean {
		return !!this._iterateElement((xn) => xml.filterHasExpression(xn));
	}

	/**
	 * Additional logic to modify query based on Access filters if user is a hybrid or host user
	 */
	override importElements(newElements: Element[], beforeElement?: Element): void {
		super.importElements(newElements, beforeElement);

		const { userCompanyUrl, userSupplierUrl } = sessionConstants;
		const rootElement = this.element;
		const accessElements = [];

		util.traverseNode(rootElement, (element) => {
			if (element.tagName == xml.FILTER_TAG_ACCESS) {
				// if access element exists (whether replaced or not), then mark query as server only as access checks cannot be done on the client
				this.bServerOnly = true;

				// if access element is checking for supplier, then prune if current user is not associated with a supplier; otherwise return as a result to be modified later
				if (RE_SUPPLIER_LAST_STEP.test(element.getAttribute("Path"))) {
					if (!userSupplierUrl) {
						return util.TRAVERSE_DELETE;
					} else if (userCompanyUrl) {
						return util.TRAVERSE_RESULT;
					}
				}
			}
		}, false, accessElements);

		// replace Access tag with appropriate operation for supplier access
		accessElements.forEach((element) => {
			this.element = element.parentNode;
			this.element.removeChild(element);
			this.AccessSupplier(element.getAttribute("Path"));
		});

		// restore original element
		this.element = rootElement;
	}

	/**
	 * Track if query can only be performed by the server and QRA
	 */
	override importQuery(query: Query, beforeElement?: Element): void {
		super.importQuery(query, beforeElement);

		if (query) {
			this.bServerOnly = this.bServerOnly || query.bServerOnly;
			this.bDirect = this.bDirect || query.bDirect;
		}
	}

	/**
	 * Add type filter if there are no other type filters already in query that do not use a path
	 */
	setType(type: C8NodeType): this {
		const e = this._iterateElement((xn) => (xn.getAttribute(xml.FILTER_PROP_PARAMETER) === xml.PROP_TYPE) && !xn.getAttribute(xml.FILTER_PROP_PATH));

		if (!e) {
			this.Type(type);
		}

		return this;
	}

	/**
	 * Convert any extended paths to standard paths
	 */
	stripExtendedPaths(): this {
		this._iterateElement((xn) => {
			const p = xn.getAttribute("Path");
			if (p) {
				xn.setAttribute("Path", convertToStandardPath(p));
			}
		});

		return this;
	}

	/**
	 * Return new query node representing an AND condition
	 */
	And(): Query {
		return this._newChild(xml.FILTER_TAG_AND);
	}

	/**
	 * Return new query node representing an OR condition
	 */
	Or(): Query {
		return this._newChild(xml.FILTER_TAG_OR);
	}

	/**
	 * Return new query node representing an NOT condition
	 */
	Not(): Query {
		return this._newChild(xml.FILTER_TAG_NOT);
	}

	/**
	 * Add query condition that tests for read access along the given path ("Read" is only right supported currently)
	 */
	Access(right: string, path: string): this {
		console.assert(right === xml.RIGHT_READ);
		const node = xml.e(this.doc, xml.FILTER_TAG_ACCESS, this.element);
		node.setAttribute(xml.FILTER_PROP_RIGHT, right);
		this._setProperty(node, xml.FILTER_PROP_PATH, path);
		this.bServerOnly = true;
		return this;
	}

	/**
	 * Apply appropriate operation to test access to a supplier along a given path
	 * @param {string} path - path to Supplier
	 * @return {Query} returns this to allow for chaining
	 */
	AccessSupplier(path: string): this {
		const { isAgent, userSupplierUrl } = sessionConstants;

		if (isAgent) {
			// the test for agent is a bit more complex because we want to match the agent or any of its suppliers;
			// we do this by extending the path to the supplier with a parent step indicating any suppliers that
			// reference the path supplier through their __Suppliers__ relationship, which includes the Supplier itself
			// and its Agent (if any)
			this.Node(xml.PROP_URL, xml.OP_EQUALS, userSupplierUrl, path + AGENT_PATH_SUFFIX);
			this.bServerOnly = true;
		} else {
			this.RefAttr(ATTR_SUPPLIER, xml.OP_EQUALS, userSupplierUrl, pathRemoveStep(path));
			this.bServerOnly = true;
		}

		return this;
	}

	/**
	 * Add query filter based on node property
	 */
	Node(prop: string, op: string, value?: any, path?: string): this {
		// ensure that property and operation are valid, and a value is specified when property is Type
		if (prop && op && (value || (prop !== xml.PROP_TYPE))) {
			const node = xml.e(this.doc, xml.FILTER_TAG_NODE, this.element);
			node.setAttribute(xml.FILTER_PROP_PARAMETER, xml.propertyToFilterParameter(prop));
			node.setAttribute(xml.FILTER_PROP_OP, op);
			this._setProperty(node, xml.FILTER_PROP_PATH, path);
			this._setValueProperty(node, op, xml.FILTER_VALUE, value);
		} else {
			// invalid parameters
			console.assert(false, `Invalid parameters: ${prop} ${op} ${value}`);
		}

		return this;
	}

	/**
	 * Add query filter for attribute that references the given URL value
	 */
	RefAttr(id: string, op: string, value?: any, path?: string): this {
		return this.Attr2(id, op, {
			Path: path,
			RValue: value,
		});
	}

	/**
	 * Add query filter for attribute
	 */
	Attr(id: string, op: string, value?: any, path?: string, index?: string | number): this {
		const params: QueryFilterParams = {};

		if (path) {
			params.Path = path;
		}
		if (index != null) {
			params.Index = index;
		}

		if (value != null) {
			if (typeof value === "number") {
				params.DValue = value;
			} else if (typeof value === "string") {
				params.SValue = value;
			} else {
				params.Value = value;
			}
		}

		return this.Attr2(id, op, params);
	}

	/**
	 * Add query filter using API that supports all varities of attribute conditions
	 */
	Attr2(id: string, op: string, params?: QueryFilterParams): this {
		const node = xml.e(this.doc, xml.FILTER_TAG_ATTR, this.element);

		// convert LocalizedName* filter to appropriate server syntax
		if (id == xml.FILTER_LOCALIZED_NAME) {
			id = xml.ATTR_LOCALIZED_NAME;

			if (params && (params.NValue == null)) {
				params.NValue = params.SValue || params.Value || "";
			}
		}

		node.setAttribute(xml.FILTER_PROP_ID, id);
		node.setAttribute(xml.FILTER_PROP_OP, op);

		if (params) {
			this._setProperty(node, xml.FILTER_PROP_DIMENSION, params[xml.FILTER_PROP_DIMENSION]);
			this._setProperty(node, xml.FILTER_PROP_FLAG, params[xml.FILTER_PROP_FLAG]);
			this._setProperty(node, xml.FILTER_PROP_INDEX, params[xml.FILTER_PROP_INDEX]);
			this._setProperty(node, xml.FILTER_PROP_PATH, params[xml.FILTER_PROP_PATH]);

			if (xml.opHasValue(op)) {
				// IMPORTANT: make sure that NValue overrides all others so LocalizedName filtering will be applied correctly
				this._setValueProperty(node, op, xml.FILTER_NVALUE, params.NValue) ||
					this._setValueProperty(node, op, xml.FILTER_VALUE, params.Value) ||
					this._setValueProperty(node, op, xml.FILTER_SVALUE, params.SValue) ||
					this._setValueProperty(node, op, xml.FILTER_RVALUE, params.RValue) ||
					this._setValueProperty(node, op, xml.FILTER_DVALUE, params.DValue);
			}
		}

		return this;
	}

	/**
	 * Add query condition to filter by given type
	 */
	Type(type: C8NodeType): this {
		return this.Node(xml.PROP_TYPE, xml.OP_EQUALS, type);
	}

	/**
	 * Add query condition to sort by an attribute
	 */
	SortByAttr(id: C8AttributeId, path?: string, index?: string, descending?: boolean): this {
		// FIX: for now there is no support to sort by LocalizedName/Node Name combination, so revert to just node name
		if (id === xml.FILTER_LOCALIZED_NAME) {
			id = xml.ATTR_NAME;
			index = null;
		}

		this._addSort(xml.FILTER_TAG_SORT_ATTR, xml.FILTER_PROP_ID, id, path, index, descending);
		return this;
	}

	/**
	 * Add query condition to sort results based on their index in the specified container attribute
	 */
	SortByIndex(id: C8AttributeId, path?: string, descending?: boolean): this {
		this._addSort(xml.FILTER_TAG_SORT_INDEX, xml.FILTER_PROP_ID, id, path, null, descending);
		this.bServerOnly = true;
		return this;
	}

	/**
	 * Add query condition to sort by a node property
	 */
	SortByNode(prop: string, path?: string, descending?: boolean): this {
		this._addSort(xml.FILTER_TAG_SORT_NODE, xml.FILTER_PROP_PARAMETER, xml.propertyToFilterParameter(prop), path, null, descending);

		if (xml.propertyType(prop) === xml.ATTR_REF) {
			this.bServerOnly = true;
		}

		return this;
	}

	/**
	 * Add a sort condition to query
	 */
	_addSort(tag: string, attrId: string, attrValue: string, path?: string, index?: string, descending?: boolean): void {
		const node = xml.e(this.doc, tag, this.element);
		node.setAttribute(attrId, attrValue);
		this._setProperty(node, xml.FILTER_PROP_PATH, path);
		this._setProperty(node, xml.FILTER_PROP_INDEX, index);

		if (descending) {
			node.setAttribute(xml.SORT_SEQUENCE, xml.SORT_DESC);
		}
	}

	/**
	 * Return new Query node for child element with given tag
	 */
	_newChild(tagName: string): Query {
		const { doc, element } = this;
		return new Query(doc, xml.e(doc, tagName, element));
	}
};

export = Query;